/**
 * CardExtrasMenu - Dropdown menu for card actions
 */
import { uiLogger } from '@annote/core/utils/debug';
import React, { useCallback, useContext, useMemo } from 'react';
import { Note } from '@annote/types';
import { useBrainContext } from 'src/context/BrainContext';
import { ShownIdsContext } from 'src/context/ShownContext';
import { ReactComponent as PlusIcon } from 'src/components/Icon/icons/plus.svg';
import { ReactComponent as ShowBacklinksIcon } from 'src/components/Icon/icons/show_backlinks.svg';
import { ReactComponent as ShowLinksIcon } from 'src/components/Icon/icons/show_links.svg';
import { BrushCleaning } from 'lucide-react';
import { ReactComponent as TrashIcon } from 'src/components/Icon/icons/trash.svg';
import { MenuContainer, MenuItem, MenuProps } from './MenuComponents';

const CardExtrasMenu: React.FC<MenuProps> = ({ note, closeMenu, triggerRef }) => {
  const { createTakeaway, deleteNotes } = useBrainContext();
  const { showAbeforeB, showAafterB, hide, shownIds } = useContext(ShownIdsContext);

  const createBacklink = (n: Note): void => {
    const newNote = createTakeaway(n);
    showAbeforeB(newNote.id, n.id);
    if (closeMenu) closeMenu();
  };

  const showBacklinks = (n: Note): void => {
    showAafterB(n.backlinks, n.id);
    if (closeMenu) closeMenu();
  };

  const showLinks = (n: Note): void => {
    showAbeforeB(n.links, n.id);
    if (closeMenu) closeMenu();
  };

  const hideNote = (n: Note): void => {
    hide(n.id);
    if (closeMenu) closeMenu();
  };

  const hideOthers = (n: Note): void => {
    hide(shownIds.filter((id) => id !== n.id));
    if (closeMenu) closeMenu();
  };

  const deleteNote = useCallback(() => {
    hide(note.id);
    deleteNotes([note]);
    if (closeMenu) closeMenu();
  }, [note, deleteNotes, hide, closeMenu]);

  const isHideOthersDisabled = useMemo(() => {
    return shownIds.length === 1;
  }, [shownIds]);

  const isShowLinksDisabled = useMemo(() => {
    const hiddenLinks = note.links.filter((id) => !shownIds.includes(id));
    return hiddenLinks.length === 0;
  }, [note.links, shownIds]);

  const isShowBacklinksDisabled = useMemo(() => {
    const hiddenBacklinks = note.backlinks.filter((id) => !shownIds.includes(id));
    return hiddenBacklinks.length === 0;
  }, [note.backlinks, shownIds]);

  uiLogger.debug('dropdown menu opened', note.id);
  const iconSizePx = 18;
  return (
    <MenuContainer triggerRef={triggerRef!}>
      <MenuItem onClick={() => createBacklink(note)}>
        <PlusIcon width={iconSizePx} height={iconSizePx} />
        New backlink note
      </MenuItem>
      <MenuItem disabled={isShowBacklinksDisabled} onClick={() => showBacklinks(note)}>
        <ShowBacklinksIcon width={iconSizePx} height={iconSizePx} />
        Show all backlinks
      </MenuItem>
      <MenuItem disabled={isShowLinksDisabled} onClick={() => showLinks(note)}>
        <ShowLinksIcon width={iconSizePx} height={iconSizePx} />
        Show all links
      </MenuItem>
      <MenuItem onClick={() => hideNote(note)}>
        <BrushCleaning size={iconSizePx} />
        Hide this note
      </MenuItem>
      <MenuItem disabled={isHideOthersDisabled} onClick={() => hideOthers(note)}>
        <BrushCleaning size={iconSizePx} />
        Hide all others
      </MenuItem>
      <MenuItem onClick={deleteNote}>
        <TrashIcon width={iconSizePx} height={iconSizePx} />
        Delete
      </MenuItem>
    </MenuContainer>
  );
};

export default CardExtrasMenu;
